import { useEffect, useRef, useState, type ReactNode, type RefObject, type SVGProps } from "react";
import { Link } from "react-router-dom";

import { LANDING_V2_CTA, LANDING_V2_PATH } from "./routes";
import "./landing-v2.css";

const FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Outfit:wght@500;600;700;800&family=Source+Sans+3:wght@400;500;600;700&display=swap";

function useLandingFonts() {
  useEffect(() => {
    const existing = document.querySelector<HTMLLinkElement>(`link[data-lv2-fonts="1"]`);
    if (existing) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = FONT_HREF;
    link.setAttribute("data-lv2-fonts", "1");
    document.head.appendChild(link);
  }, []);
}

function useSmoothScroll() {
  useEffect(() => {
    const root = document.documentElement;
    const previous = root.style.scrollBehavior;
    root.style.scrollBehavior = "smooth";
    return () => {
      root.style.scrollBehavior = previous;
    };
  }, []);
}

function useRevealOnScroll(rootRef: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      root.querySelectorAll(".lv2-reveal").forEach((el) => el.classList.add("is-visible"));
      return;
    }
    const nodes = Array.from(root.querySelectorAll<HTMLElement>(".lv2-reveal"));
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.14, rootMargin: "0px 0px -8% 0px" },
    );
    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, [rootRef]);
}

function useHeroParallax(enabled: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const onMove = (event: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width - 0.5;
      const y = (event.clientY - rect.top) / rect.height - 0.5;
      el.style.transform = `translate3d(${x * 12}px, ${y * 10}px, 0)`;
    };
    const onLeave = () => {
      el.style.transform = "translate3d(0, 0, 0)";
    };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, [enabled]);
  return ref;
}

function Icon({ children, ...props }: SVGProps<SVGSVGElement> & { children: ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true" {...props}>
      {children}
    </svg>
  );
}

function HeroUiMockup({ testId }: { testId?: string }) {
  return (
    <div
      className="lv2-mock"
      data-testid={testId}
      aria-label="Zephix task board and timeline preview"
    >
      <div className="lv2-mock-top">
        <span className="lv2-dots" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>
        <span>Zephix · Product launch workspace</span>
      </div>
      <div className="lv2-mock-body">
        <aside className="lv2-mock-side" aria-hidden="true">
          <b>Views</b>
          <div className="on">Board</div>
          <div>Timeline</div>
          <div>Dashboard</div>
          <div>My work</div>
        </aside>
        <div className="lv2-mock-main">
          <div className="lv2-board" aria-hidden="true">
            <div className="lv2-col">
              <h4>To do</h4>
              <div className="lv2-card-mini">
                <strong>Draft launch brief</strong>
                <span>Product · Today</span>
              </div>
              <div className="lv2-card-mini">
                <strong>Map stakeholders</strong>
                <span>Owner · Maya</span>
              </div>
            </div>
            <div className="lv2-col">
              <h4>In progress</h4>
              <div className="lv2-card-mini">
                <strong>Ship onboarding flow</strong>
                <span>Design + Eng</span>
                <span className="lv2-pill">On track</span>
              </div>
              <div className="lv2-card-mini">
                <strong>Align pricing copy</strong>
                <span>Due Fri</span>
              </div>
            </div>
            <div className="lv2-col">
              <h4>Done</h4>
              <div className="lv2-card-mini">
                <strong>Choose template</strong>
                <span>Completed</span>
              </div>
            </div>
          </div>
          <div className="lv2-timeline" aria-hidden="true">
            <div className="lv2-timeline-bar">
              <span />
            </div>
            <p>Timeline · Sprint goal 68% complete</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function DashboardPreview() {
  return (
    <div className="lv2-dash" aria-hidden="true">
      <div className="lv2-dash-row">
        <div className="lv2-stat">
          <span>Active work</span>
          <strong>24</strong>
        </div>
        <div className="lv2-stat">
          <span>At risk</span>
          <strong>2</strong>
        </div>
        <div className="lv2-stat">
          <span>Done this week</span>
          <strong>11</strong>
        </div>
      </div>
      <div className="lv2-chart">
        <i />
        <i />
        <i />
        <i />
        <i />
      </div>
    </div>
  );
}

const FEATURES = [
  {
    title: "Plan Smarter",
    body: "Start from a clear plan—not a blank board. Organize work in minutes so your team knows what matters first.",
    icon: (
      <Icon>
        <path d="M4 6.5h16M4 12h10M4 17.5h7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </Icon>
    ),
  },
  {
    title: "Stay Aligned",
    body: "Keep priorities visible for PMs, product owners, and makers—so status updates stop living in chat threads.",
    icon: (
      <Icon>
        <circle cx="8" cy="10" r="3" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="16" cy="10" r="3" stroke="currentColor" strokeWidth="1.8" />
        <path d="M5 18c.8-2 2.4-3 3-3s2.2 1 3 3M13 18c.8-2 2.4-3 3-3s2.2 1 3 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </Icon>
    ),
  },
  {
    title: "Move Faster",
    body: "Lightweight workflows help small teams ship without drowning in process. Update work where it already lives.",
    icon: (
      <Icon>
        <path d="M5 13 12 4l2.5 5H19l-7 9 1-5H5z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      </Icon>
    ),
  },
  {
    title: "See the Big Picture",
    body: "Boards, timelines, and a simple dashboard give you the story of the project—without another spreadsheet.",
    icon: (
      <Icon>
        <path d="M4 19V5M4 19h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M8 15v-4M12 15V8M16 15v-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </Icon>
    ),
  },
  {
    title: "Collaborate Clearly",
    body: "Owners, due dates, and next steps stay attached to the work so handoffs feel calm—not chaotic.",
    icon: (
      <Icon>
        <path d="M7 8h10M7 12h7M7 16h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" strokeWidth="1.8" />
      </Icon>
    ),
  },
  {
    title: "Adapt Easily",
    body: "Change scope, reshuffle priorities, and keep moving. Zephix stays flexible as your startup evolves.",
    icon: (
      <Icon>
        <path d="M4 12a8 8 0 0 1 13.5-5.8M20 12a8 8 0 0 1-13.5 5.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M17 3.5V7h-3.5M7 20.5V17H10.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </Icon>
    ),
  },
] as const;

const GOVERNANCE_PILLARS = [
  {
    title: "Clear ownership",
    body: "Every decision has a name on it. No more “I thought someone else was handling that.”",
    icon: (
      <Icon>
        <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.8" />
        <path d="M5 20c.8-3.2 3.4-5 7-5s6.2 1.8 7 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </Icon>
    ),
  },
  {
    title: "Risks stay visible",
    body: "Spot blockers early—attached to the work, not buried in a spreadsheet no one opens.",
    icon: (
      <Icon>
        <path d="M12 3 2 19h20L12 3z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="M12 10v4M12 16h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </Icon>
    ),
  },
  {
    title: "Approvals with context",
    body: "Checkpoints include the why—not just a green checkmark. Evidence travels with the work.",
    icon: (
      <Icon>
        <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="4" y="4" width="16" height="16" rx="4" stroke="currentColor" strokeWidth="1.8" />
      </Icon>
    ),
  },
  {
    title: "Audit-ready by default",
    body: "A lightweight trail of what changed, when, and who approved it—without extra admin work.",
    icon: (
      <Icon>
        <path d="M6 4h12v16H6z" stroke="currentColor" strokeWidth="1.8" />
        <path d="M9 8h6M9 12h6M9 16h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </Icon>
    ),
  },
] as const;

function WhyInfographic() {
  return (
    <div className="lv2-info-strip lv2-reveal" aria-hidden="true" data-testid="lv2-why-infographic">
      <div className="lv2-info-node">
        <span className="lv2-info-icon">1</span>
        <strong>Plan</strong>
        <span>Owners set</span>
      </div>
      <div className="lv2-info-connector" />
      <div className="lv2-info-node">
        <span className="lv2-info-icon">2</span>
        <strong>Align</strong>
        <span>Team sees it</span>
      </div>
      <div className="lv2-info-connector" />
      <div className="lv2-info-node">
        <span className="lv2-info-icon">3</span>
        <strong>Ship</strong>
        <span>Progress tracked</span>
      </div>
    </div>
  );
}

function GovernanceFlowInfographic() {
  return (
    <div
      className="lv2-gov-flow lv2-reveal"
      aria-label="Governance flow: plan work, flag risks, approve with evidence, ship with a trail"
      data-testid="lv2-governance-infographic"
    >
      <div className="lv2-gov-flow-track">
        <div className="lv2-gov-flow-step">
          <span className="lv2-gov-flow-dot" />
          <strong>Plan the work</strong>
          <span>Templates & owners</span>
        </div>
        <div className="lv2-gov-flow-step">
          <span className="lv2-gov-flow-dot is-risk" />
          <strong>Flag what’s at risk</strong>
          <span>Visible on the board</span>
        </div>
        <div className="lv2-gov-flow-step">
          <span className="lv2-gov-flow-dot is-approve" />
          <strong>Approve with evidence</strong>
          <span>Context, not checkbox theater</span>
        </div>
        <div className="lv2-gov-flow-step">
          <span className="lv2-gov-flow-dot is-done" />
          <strong>Ship with a trail</strong>
          <span>Decisions stay findable</span>
        </div>
      </div>
      <div className="lv2-gov-flow-stats">
        <div>
          <strong>3×</strong>
          <span>fewer “who approved this?” pings</span>
        </div>
        <div>
          <strong>100%</strong>
          <span>of checkpoints tied to work</span>
        </div>
      </div>
    </div>
  );
}

export function LandingV2Page() {
  const siteRef = useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(false);
  const parallaxRef = useHeroParallax(true);

  useLandingFonts();
  useSmoothScroll();
  useRevealOnScroll(siteRef);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="lv2-site" ref={siteRef} data-testid="lv2-landing" data-path={LANDING_V2_PATH}>
      <a className="lv2-skip" href="#lv2-main">
        Skip to content
      </a>

      <header className={`lv2-nav${scrolled ? " is-scrolled" : ""}`}>
        <div className="lv2-wrap lv2-nav-inner">
          <Link className="lv2-brand" to={LANDING_V2_PATH} aria-label="Zephix home">
            <span className="lv2-brand-mark" aria-hidden="true" />
            Zephix
          </Link>
          <ul className="lv2-nav-links">
            <li>
              <a href="#why">Why Zephix</a>
            </li>
            <li>
              <a href="#features">Features</a>
            </li>
            <li>
              <a href="#governance">Governance</a>
            </li>
            <li>
              <a href="#ui-preview">Product</a>
            </li>
            <li>
              <a href="#pricing">Pricing</a>
            </li>
          </ul>
          <div className="lv2-nav-cta">
            <Link className="lv2-btn lv2-btn-ghost" to="/login">
              Log in
            </Link>
            <Link className="lv2-btn lv2-btn-primary" to={LANDING_V2_CTA.trial}>
              Start free trial
            </Link>
          </div>
        </div>
      </header>

      <main id="lv2-main">
        <section className="lv2-hero" aria-labelledby="lv2-hero-heading">
          <div className="lv2-wrap lv2-hero-grid">
            <div className="lv2-reveal is-visible">
              <p className="lv2-hero-brand">Zephix</p>
              <h1 id="lv2-hero-heading">Project clarity for small teams that move fast</h1>
              <p className="lv2-hero-lead">
                A clean workspace for project managers, product managers, and product owners—plan
                work, stay aligned, and ship without the enterprise bloat.
              </p>
              <div className="lv2-cta-row">
                <Link className="lv2-btn lv2-btn-primary lv2-btn-lg" to={LANDING_V2_CTA.trial}>
                  Start Your 6-Month Free Trial
                </Link>
                <a className="lv2-btn lv2-btn-secondary lv2-btn-lg" href={LANDING_V2_CTA.demoHash}>
                  Watch the 90-Second Demo
                </a>
              </div>
              <p className="lv2-hero-note">No credit card required · Full access · Cancel anytime</p>
            </div>

            <div className="lv2-hero-visual lv2-reveal is-visible" ref={parallaxRef}>
              <div className="lv2-hero-glow" aria-hidden="true" />
              <HeroUiMockup testId="lv2-hero-mock" />
            </div>
          </div>
        </section>

        <section
          className="lv2-section lv2-section-alt lv2-section-tight-top"
          id="why"
          aria-labelledby="lv2-why-heading"
        >
          <div className="lv2-wrap lv2-section-split">
            <div className="lv2-section-split-copy">
              <p className="lv2-eyebrow lv2-reveal">Why Zephix</p>
              <h2 id="lv2-why-heading" className="lv2-reveal">
                Built for small teams—not enterprise theater
              </h2>
              <p className="lv2-section-lead lv2-reveal">
                Early-stage startups need clarity, not complexity. Zephix keeps planning simple, status
                honest, and decisions close to the work so you can focus on shipping.
              </p>
            </div>
            <WhyInfographic />
            <div className="lv2-why-grid lv2-section-split-full">
              <article className="lv2-why-item lv2-reveal">
                <h3>Simplicity</h3>
                <p>Open Zephix and get a working view of your project in minutes—not after a week of setup.</p>
              </article>
              <article className="lv2-why-item lv2-reveal">
                <h3>Clarity</h3>
                <p>Everyone sees owners, priorities, and progress in one place. Fewer “where does this live?” moments.</p>
              </article>
              <article className="lv2-why-item lv2-reveal">
                <h3>Speed</h3>
                <p>Lightweight by design. Update work quickly, stay in flow, and keep your team moving together.</p>
              </article>
            </div>
          </div>
        </section>

        <section
          className="lv2-section lv2-section-center"
          id="features"
          aria-labelledby="lv2-features-heading"
        >
          <div className="lv2-wrap">
            <p className="lv2-eyebrow lv2-reveal">What you get</p>
            <h2 id="lv2-features-heading" className="lv2-reveal">
              Everything a lean team needs—nothing you don’t
            </h2>
            <p className="lv2-section-lead lv2-reveal">
              Six essentials that help PMs and product leaders stay on the same page without heavy
              process.
            </p>
            <div className="lv2-features">
              {FEATURES.map((feature) => (
                <article key={feature.title} className="lv2-feature lv2-reveal">
                  <div className="lv2-feature-icon">{feature.icon}</div>
                  <h3>{feature.title}</h3>
                  <p>{feature.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section
          className="lv2-section lv2-section-alt"
          id="governance"
          aria-labelledby="lv2-governance-heading"
        >
          <div className="lv2-wrap lv2-governance-layout">
            <div className="lv2-governance-copy lv2-reveal">
              <p className="lv2-eyebrow">Governance that fits your pace</p>
              <h2 id="lv2-governance-heading">
                Stay in control as you grow—without slowing down
              </h2>
              <p className="lv2-section-lead">
                Small teams still need good decisions. Zephix builds lightweight governance into how
                work runs: clear owners, visible risks, and approvals that come with context—not
                another layer of bureaucracy.
              </p>
              <p className="lv2-governance-note">
                Governance should protect momentum, not kill it. Zephix keeps the guardrails close to
                the work so PMs and product owners always know what needs a call.
              </p>
            </div>
            <GovernanceFlowInfographic />
            <div className="lv2-governance-grid">
              {GOVERNANCE_PILLARS.map((pillar) => (
                <article key={pillar.title} className="lv2-governance-card lv2-reveal">
                  <div className="lv2-governance-card-icon">{pillar.icon}</div>
                  <h3>{pillar.title}</h3>
                  <p>{pillar.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section
          className="lv2-section lv2-section-center"
          id="ui-preview"
          aria-labelledby="lv2-preview-heading"
        >
          <div className="lv2-wrap">
            <p className="lv2-eyebrow lv2-reveal">Product tour</p>
            <h2 id="lv2-preview-heading" className="lv2-reveal">
              Intuitive views you’ll actually use
            </h2>
            <p className="lv2-section-lead lv2-reveal">
              Boards, timelines, and dashboards that feel familiar on day one—designed to be easy on
              the eyes and quick to scan.
            </p>
            <div className="lv2-preview-grid">
              <figure className="lv2-preview-card lv2-reveal">
                <HeroUiMockup />
                <figcaption>
                  <strong>Task board + timeline</strong>
                  Drag priorities into place and watch the timeline stay in sync as your plan evolves.
                </figcaption>
              </figure>
              <figure className="lv2-preview-card lv2-reveal">
                <DashboardPreview />
                <figcaption>
                  <strong>Snapshot dashboard</strong>
                  Spot what’s active, what’s at risk, and what shipped—without digging through filters.
                </figcaption>
              </figure>
            </div>
          </div>
        </section>

        <section className="lv2-section lv2-section-alt" id="founder" aria-labelledby="lv2-founder-heading">
          <div className="lv2-wrap lv2-founder">
            <div className="lv2-founder-card lv2-reveal">
              <p id="lv2-founder-heading">
                “We built Zephix because great teams deserve clarity—without buying an enterprise
                stack to get it.”
              </p>
            </div>
            <div className="lv2-founder-copy lv2-reveal">
              <p className="lv2-eyebrow">Founder story</p>
              <h2>Why Zephix exists</h2>
              <p>
                Too many project tools feel either toy-like or overwhelming. Founders and product
                leaders were stitching together boards, docs, and chat—then losing the plot when
                priorities shifted.
              </p>
              <p>
                Zephix started as a quieter alternative: a place where small teams can plan clearly,
                see progress honestly, and adapt quickly. Friendly language. Fast screens. Just enough
                structure to keep everyone aligned.
              </p>
            </div>
          </div>
        </section>

        <section
          className="lv2-section lv2-section-center"
          id="pricing"
          aria-labelledby="lv2-pricing-heading"
        >
          <div className="lv2-wrap">
            <p className="lv2-eyebrow lv2-reveal">Pricing preview</p>
            <h2 id="lv2-pricing-heading" className="lv2-reveal">
              Try Zephix free for 6 months
            </h2>
            <p className="lv2-section-lead lv2-reveal">
              Full access while you build the habit. No surprises—just time to see if Zephix fits your
              team.
            </p>
            <div className="lv2-pricing lv2-reveal">
              <p className="price-label">Startup trial</p>
              <h3>6 months free</h3>
              <p>Everything you need to run projects with a lean team.</p>
              <ul className="lv2-price-list">
                <li>No credit card required</li>
                <li>Full access to boards, timelines, and dashboards</li>
                <li>Cancel anytime</li>
              </ul>
              <Link className="lv2-btn lv2-btn-primary lv2-btn-lg" to={LANDING_V2_CTA.trial}>
                Start Your 6-Month Free Trial
              </Link>
            </div>
          </div>
        </section>

        <section className="lv2-final" aria-labelledby="lv2-final-heading">
          <div className="lv2-wrap">
            <div className="lv2-final-panel lv2-reveal">
              <h2 id="lv2-final-heading">Ready to make work feel organized?</h2>
              <p>
                Join small teams using Zephix to plan smarter, stay aligned, and move faster—starting
                today.
              </p>
              <div className="lv2-cta-row">
                <Link className="lv2-btn lv2-btn-primary lv2-btn-lg" to={LANDING_V2_CTA.trial}>
                  Start Your 6-Month Free Trial
                </Link>
                <a className="lv2-btn lv2-btn-secondary lv2-btn-lg" href={LANDING_V2_CTA.demoHash}>
                  Watch the 90-Second Demo
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="lv2-footer" data-testid="lv2-footer">
        <div className="lv2-wrap lv2-footer-inner">
          <Link className="lv2-brand" to={LANDING_V2_PATH}>
            <span className="lv2-brand-mark" aria-hidden="true" />
            Zephix
          </Link>
          <ul className="lv2-footer-links">
            <li>
              <Link to={LANDING_V2_CTA.product}>Product</Link>
            </li>
            <li>
              <a href={LANDING_V2_CTA.pricing}>Pricing</a>
            </li>
            <li>
              <Link to={LANDING_V2_CTA.support}>Support</Link>
            </li>
            <li>
              <Link to={LANDING_V2_CTA.terms}>Terms</Link>
            </li>
            <li>
              <Link to={LANDING_V2_CTA.privacy}>Privacy</Link>
            </li>
            <li>
              <Link to={LANDING_V2_CTA.contact}>Contact</Link>
            </li>
          </ul>
          <p className="lv2-footer-copy">© {new Date().getFullYear()} Zephix. Built for small teams.</p>
        </div>
      </footer>
    </div>
  );
}
